import React from "react";
import { required, isValidRegex, grtrThanEqTo, lessThanEqTo, } from "../../utils/validation";
import { integerExp, alphaNumExp, whiteSpaceCheck } from "../../utils/regex";
const InputField = (props) => {
    // sets the default value of input if present
    // value: value of the input (type = string)
    // setValue: function to set value
    const [value, setValue] = React.useState(props.defaultValue || "");
    // if default value is updated from outside, change it inside also
    React.useEffect(() => {
        if (props.defaultValue !== undefined && value !== props.defaultValue) {
            setValue(props.defaultValue);
        }
    }, [props.defaultValue]);
    React.useEffect(() => {
        if (props.value !== undefined && value !== props.value) {
            setValue(props.value);
        }
    }, [props.value]);
    // when focussed, this helps to colour the border of input_field_cont
    const [focusClass, setFocusClass] = React.useState("");
    const [disablePaste, setDisablePaste] = React.useState(false);
    /**
     * @param value The string that needs to be checked for integer
     * @returns If integer need not be checked, then return true.
     * - If integer needs to be checked, then
     * - If value is valid integer, then return true; otherwise, return false
     */
    function isValidInteger(value) {
        return (!props.integer || (props.integer && !isValidRegex(value, integerExp)));
    }
    /**
     * @param value The string that needs to be checked for float
     * @returns If float need not be checked, then return true.
     * - If float needs to be checked, then
     * - if value is valid float, then return true; otherwise, return false
     */
    function isValidFloat(value) {
        return !props.float || (props.float && !isValidRegex(value, props.float));
    }
    /**
     * @param value The string that needs to be checked for alphanumeric characters
     * @returns If alphanum need not be checked, then return true.
     * - If alphanum needs to be checked, then
     * - if value is valid alphanum, then return true; otherwise, return false
     */
    function isValidAlphaNum(value) {
        return (!props.alphanum || (props.alphanum && !isValidRegex(value, alphaNumExp)));
    }
    /**
     * @param value The string that needs to be checked for maxlength
     * @returns If maxlength need not be checked, then return true.
     * - If maxlength needs to be checked, then
     * - if value has less characters than maxlength, then return true; otherwise, return false
     */
    function isValidMaxLength(value) {
        return props.maxLength === undefined || value.length <= props.maxLength;
    }
    function isValidMaxLengthBeforeDot(value) {
        return ((props === null || props === void 0 ? void 0 : props.maxLengthBeforeDot) === undefined ||
            parseInt(value).toString().length <= (props === null || props === void 0 ? void 0 : props.maxLengthBeforeDot));
    }
    /**
     * @param value The string that needs to be checked for minlength
     * @returns If minlength need not be checked, then return true.
     * - If minlength needs to be checked, then
     * - if value has less characters than minlength, then return true; otherwise, return false
     */
    function isValidMinLength(value) {
        return props.minLength === undefined || value.length < props.minLength;
    }
    /**
     * @param value The value that is required
     * @returns If required need not be checked, then return true.
     * - If required needs to be checked, return true if value is not present; false if value is present
     */
    function isRequired(value) {
        return !props.required || (props.required && !!required(value));
    }
    /**
     * @param value The value that is to be validated
     * @returns If min need not be checked, then return true.
     * - If min needs to be checked,
     * - throw error if value is string
     * - return true if value < min; false if value >= min
     */
    function isLessThanMin(value) {
        if (props.min === undefined) {
            return true;
        }
        if (isNaN(+value)) {
            throw Error("Can't validate non-numerical value for min");
        }
        return !!grtrThanEqTo(+value, props.min);
    }
    /**
     * @param value The value that is to be validated
     * @returns If max need not be checked, then return true.
     * - If max needs to be checked,
     * - throw error if value is string
     * - return true if value > max; false if value <= min
     */
    function isGrtrThanMax(value) {
        if (props.max === undefined) {
            return true;
        }
        if (isNaN(+value)) {
            throw Error("Can't validate non-numerical value for max");
        }
        return !!lessThanEqTo(+value, props.max);
    }
    /**
     * @param value The string that needs to be checked for the pattern
     * @returns If pattern need not be checked, then return true.
     * - If pattern needs to be checked, then
     * - if value is valid pattern, then return true; otherwise, return false
     */
    function isValidPattern(value) {
        return (!props.pattern || (props.pattern && !isValidRegex(value, props.pattern)));
    }
    /**
     * @param value input value gets checked with shouldBeDifferentValue-> True
     *  else false
     *  @returns true
     */
    function stringMatching(valueA, valueB) {
        return (!props.shouldBeDifferentValue ||
            (props.shouldBeDifferentValue && valueA === valueB));
    }
    function AvoidWhiteSpace(value) {
        return !(!props.AvoidWhiteSpace ||
            (props.AvoidWhiteSpace && !isValidRegex(value, whiteSpaceCheck)));
    }
    /**
     * This function runs on blur.
     * This removes the focus class on input_field_cont and removes commas.
     * This function calls the onBlur callback function with all the errors.
     */
    function handleBlur(e) {
        setFocusClass("");
        let errorObj = {};
        if (props.required) {
            errorObj = Object.assign(Object.assign({}, errorObj), { required: isRequired(value) }); // set isRequired error
        }
        if (props.min) {
            errorObj = Object.assign(Object.assign({}, errorObj), { min: isLessThanMin(value) }); // set error for min
        }
        if (props.max) {
            errorObj = Object.assign(Object.assign({}, errorObj), { max: isGrtrThanMax(value) }); // set error for max
        }
        if (props.pattern) {
            errorObj = Object.assign(Object.assign({}, errorObj), { pattern: isValidPattern(value) }); // set error for pattern
        }
        if (props.shouldBeDifferentValue) {
            errorObj = Object.assign(Object.assign({}, errorObj), { shouldBeDifferentValue: stringMatching(props.shouldBeDifferentValue, value) }); //set error for string match
        }
        if (props.AvoidWhiteSpace) {
            errorObj = Object.assign(Object.assign({}, errorObj), { hasWhiteSpace: AvoidWhiteSpace(value) }); // set error for pattern
        }
        if (props.minLength) {
            errorObj = Object.assign(Object.assign({}, errorObj), { minLength: isValidMinLength(value) }); // set error for minLength
        }
        if (props.onBlur) {
            props.onBlur(errorObj, e);
        }
    }
    const handleOnInput = (e) => {
        if (e.target.value.length > props.maxLength) {
            e.target.value = e.target.value.slice(0, props.maxLength);
        }
    };
    /**
     * Checks for all the validations and then changes value accordingly
     * @param e The input event
     */
    function handleChange(e) {
        if (disablePaste)
            setValue("");
        else {
            let resolvedValue;
            if (props.type == "password") {
                e.target.value = e.target.value.split(" ").join("");
            }
            if ((e.target.value || e.target.value === "" || e.target.value === "0") &&
                isValidInteger(e.target.value) &&
                isValidFloat(e.target.value) &&
                isValidAlphaNum(e.target.value) &&
                isValidMaxLength(e.target.value) &&
                isValidMaxLengthBeforeDot(e.target.value)) {
                // if value needs to be changed then process the new value
                resolvedValue = e.target.value;
            }
            else {
                // otherwise process the previous state
                resolvedValue = value;
            }
            if (props.hasOwnProperty("onChange")) {
                const newValue = props.onChange(resolvedValue || ""); // call onchange callback
                if (newValue !== undefined) {
                    setValue(newValue); // set the new value
                }
                else {
                    setValue(resolvedValue || ""); // set the new value
                }
            }
            else {
                setValue(e.target.value);
                props.onChangeEvent && props.onChangeEvent(e);
            }
        }
        setDisablePaste(false);
    }
    const handleMouseOut = (e) => {
        if (props.onMouseOut) {
            e.currentTarget.blur();
            props.onMouseOut(e.target.value);
        }
    };
    return (React.createElement("div", { className: "col" },
        React.createElement("div", { className: props.className },
            React.createElement("div", { className: props.placeholderClassName +
                    (props.errorText ? " error" : focusClass) },
                value === "" && props.lable && (React.createElement(React.Fragment, null,
                    React.createElement("label", { htmlFor: props.id }, props.placeholder),
                    props.required && React.createElement("span", { className: "star" }, "*"))),
                value != "" && props.showLabel && (React.createElement(React.Fragment, null,
                    React.createElement("label", { htmlFor: props.id }, props.placeholder)))),
            props.prefix && React.createElement("div", { className: "input_prefix" }, props.prefix),
            React.createElement("input", Object.assign({ defaultValue: props.defaultValue ? props.defaultValue : value, value: value, step: props === null || props === void 0 ? void 0 : props.step, maxLength: props.maxLength, minLength: props.minLength, onChange: handleChange, placeholder: props.lable ? "" : props.placeholder, className: `form-control ${props.highlightInputField && props.errorText && "RedBorder"}`, onWheel: (e) => (props === null || props === void 0 ? void 0 : props.onWheel) && (props === null || props === void 0 ? void 0 : props.onWheel(e)), name: props.name, autoFocus: props.autofocus, onFocus: () => {
                    setFocusClass("focus");
                    if (props.onFocus) {
                        props.onFocus();
                    }
                }, onMouseOut: handleMouseOut, onBlur: (e) => (props.onBlur ? handleBlur(e) : null), type: props.type, id: props.id, disabled: props.disabled, autoComplete: props.autoComplete, onPaste: () => {
                    if (props.disablePaste)
                        setDisablePaste(true);
                }, required: props.required, pattern: props.inputPattern, onClick: () => props === null || props === void 0 ? void 0 : props.onClick }, (props.onKeyDown ? { onKeyDown: (e) => props.onKeyDown(e) } : {}))),
            props.children && props.children,
            props.suffix && React.createElement("div", { className: "input_suffix" }, props.suffix)),
        props.passErrorTextButHideErrTxt
            ? null
            : props.errorText && React.createElement("p", { className: "help_text red" }, props.errorText)));
};
export default InputField;
